/**
 * Project Calendar Modals
 * Contains all modal components for the Project Calendar section
 */

import { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import Select from 'react-select';

/**
 * AddEventModal - Modal for adding new calendar events
 */
export const AddEventModal = ({ show, onHide, onSave, eventTypes, teamMembers }) => {
    const [eventTitle, setEventTitle] = useState('');
    const [eventType, setEventType] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [description, setDescription] = useState('');
    const [assignedTo, setAssignedTo] = useState(null);
    const [priority, setPriority] = useState('medium');
    const [location, setLocation] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        // Validation
        if (!eventTitle.trim()) {
            setError('Event title is required');
            return;
        }
        if (!eventType) {
            setError('Event type is required');
            return;
        }
        if (!startDate) {
            setError('Start date is required');
            return;
        }
        if (endDate && new Date(endDate) < new Date(startDate)) {
            setError('End date must be after start date');
            return;
        }

        // Create new event object
        const newEvent = {
            title: eventTitle,
            type: eventType.value,
            startDate,
            startTime: startTime || '00:00',
            endDate: endDate || startDate,
            endTime: endTime || startTime || '00:00',
            description,
            assignedTo: assignedTo ? assignedTo.map(member => member.label) : [],
            priority,
            location
        };

        // Call parent handler to save event
        if (onSave) {
            onSave(newEvent);
        }
        
        handleReset();
        onHide();
    };

    const handleReset = () => {
        setEventTitle('');
        setEventType(null);
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setDescription('');
        setAssignedTo(null);
        setPriority('medium');
        setLocation('');
        setError('');
    };

    const handleClose = () => {
        handleReset();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Add Event</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Event Title <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter event title"
                                    value={eventTitle}
                                    onChange={(e) => {
                                        setEventTitle(e.target.value);
                                        setError('');
                                    }}
                                    isInvalid={!!error && !eventTitle}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Event Type <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    options={eventTypes.map(type => ({
                                        value: type.value,
                                        label: (
                                            <div className="d-flex align-items-center gap-2">
                                                <i className={`fas ${type.icon}`} style={{ color: type.color }}></i>
                                                <span>{type.label}</span>
                                            </div>
                                        ),
                                        rawLabel: type.label
                                    }))}
                                    value={eventType}
                                    onChange={(selected) => {
                                        setEventType(selected);
                                        setError('');
                                    }}
                                    placeholder="Select event type"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Priority <span className='text-danger'>*</span></Form.Label>
                                <Form.Select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Start Date <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setError('');
                                    }}
                                    isInvalid={!!error && !startDate}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Start Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setError('');
                                    }}
                                    min={startDate}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>End Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Assigned To</Form.Label>
                                <Select
                                    options={teamMembers}
                                    value={assignedTo}
                                    onChange={setAssignedTo}
                                    placeholder="Select team members"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isMulti
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Location <span className="text-muted">(Optional)</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter location or meeting link"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter event description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        {error && (
                            <Col sm={12}>
                                <div className="alert alert-danger mb-0 py-2">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                </div>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    <i className="fas fa-save me-2"></i>Save Event
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * EditEventModal - Modal for editing existing calendar events
 */
export const EditEventModal = ({ show, onHide, onSave, event, eventTypes, teamMembers }) => {
    const [eventTitle, setEventTitle] = useState('');
    const [eventType, setEventType] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [description, setDescription] = useState('');
    const [assignedTo, setAssignedTo] = useState(null);
    const [priority, setPriority] = useState('medium');
    const [location, setLocation] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (event) {
            setEventTitle(event.title || '');
            setEventType(eventTypes.find(t => t.value === event.type) || null);
            setStartDate(event.startDate || '');
            setStartTime(event.startTime || '');
            setEndDate(event.endDate || event.startDate || '');
            setEndTime(event.endTime || '');
            setDescription(event.description || '');
            setPriority(event.priority || 'medium');
            setLocation(event.location || '');
            
            // Convert assignedTo array to select format
            if (event.assignedTo) {
                const assigned = event.assignedTo.map(name => 
                    teamMembers.find(m => m.label === name) || { value: name.toLowerCase(), label: name }
                );
                setAssignedTo(assigned);
            }
        }
    }, [event, eventTypes, teamMembers]);

    const handleSubmit = () => {
        if (!eventTitle.trim()) {
            setError('Event title is required');
            return;
        }
        if (!eventType) {
            setError('Event type is required');
            return;
        }
        if (!startDate) {
            setError('Start date is required');
            return;
        }
        if (endDate && new Date(endDate) < new Date(startDate)) {
            setError('End date must be after start date');
            return;
        }

        // Update event object
        const updatedEvent = {
            ...event,
            title: eventTitle,
            type: eventType.value,
            startDate,
            startTime: startTime || '00:00',
            endDate: endDate || startDate,
            endTime: endTime || startTime || '00:00',
            description,
            assignedTo: assignedTo ? assignedTo.map(member => member.label) : [],
            priority,
            location
        };

        // Call parent handler to save changes
        if (onSave) {
            onSave(updatedEvent);
        }
        
        onHide();
    };

    if (!event) return null;

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Edit Event</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Event Title <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter event title"
                                    value={eventTitle}
                                    onChange={(e) => {
                                        setEventTitle(e.target.value);
                                        setError('');
                                    }}
                                    isInvalid={!!error && !eventTitle}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Event Type <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    options={eventTypes.map(type => ({
                                        value: type.value,
                                        label: (
                                            <div className="d-flex align-items-center gap-2">
                                                <i className={`fas ${type.icon}`} style={{ color: type.color }}></i>
                                                <span>{type.label}</span>
                                            </div>
                                        ),
                                        rawLabel: type.label
                                    }))}
                                    value={eventType}
                                    onChange={(selected) => {
                                        setEventType(selected);
                                        setError('');
                                    }}
                                    placeholder="Select event type"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Priority <span className='text-danger'>*</span></Form.Label>
                                <Form.Select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Start Date <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setError('');
                                    }}
                                    isInvalid={!!error && !startDate}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Start Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setError('');
                                    }}
                                    min={startDate}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>End Time</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Assigned To</Form.Label>
                                <Select
                                    options={teamMembers}
                                    value={assignedTo}
                                    onChange={setAssignedTo}
                                    placeholder="Select team members"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isMulti
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Location <span className="text-muted">(Optional)</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter location or meeting link"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter event description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        {error && (
                            <Col sm={12}>
                                <div className="alert alert-danger mb-0 py-2">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                </div>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    <i className="fas fa-save me-2"></i>Update Event
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * EventDetailsModal - Modal for viewing event details
 */
export const EventDetailsModal = ({ show, onHide, onEdit, onDelete, event, getEventTypeConfig, formatDate, formatTime }) => {
    if (!event) return null;

    const typeConfig = getEventTypeConfig(event.type);

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Event Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className='gy-3'>
                    <Col sm={12}>
                        <h5 className="fw-bold mb-2">{event.title}</h5>
                        <Badge 
                            bg="light" 
                            text="dark" 
                            className="fs-12"
                            style={{ 
                                borderLeft: `3px solid ${typeConfig.color}`,
                                borderRadius: '3px'
                            }}
                        >
                            <i className={`fas ${typeConfig.icon} me-1`}></i>
                            {typeConfig.label}
                        </Badge>
                    </Col>

                    <Col sm={12}>
                        <div className="border-bottom pb-2 mb-2">
                            <div className="text-muted fs-12 mb-1">Date & Time</div>
                            <div className="fw-semibold fs-14">
                                <i className="fas fa-calendar me-2 text-primary"></i>
                                {formatDate(event.startDate)}
                                {formatTime(event.startTime) && (
                                    <>
                                        <br />
                                        <i className="fas fa-clock me-2 text-primary"></i>
                                        {formatTime(event.startTime)}
                                        {event.endTime && event.endTime !== event.startTime && (
                                            <> - {formatTime(event.endTime)}</>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Col>

                    {event.location && (
                        <Col sm={12}>
                            <div className="border-bottom pb-2 mb-2">
                                <div className="text-muted fs-12 mb-1">Location</div>
                                <div className="fw-semibold fs-14">
                                    <i className="fas fa-map-marker-alt me-2 text-danger"></i>
                                    {event.location}
                                </div>
                            </div>
                        </Col>
                    )}

                    {event.priority && (
                        <Col sm={12}>
                            <div className="border-bottom pb-2 mb-2">
                                <div className="text-muted fs-12 mb-1">Priority</div>
                                <Badge bg={event.priority === 'high' ? 'danger' : event.priority === 'medium' ? 'warning' : 'success'}>
                                    {event.priority.charAt(0).toUpperCase() + event.priority.slice(1)}
                                </Badge>
                            </div>
                        </Col>
                    )}

                    {event.assignedTo && event.assignedTo.length > 0 && (
                        <Col sm={12}>
                            <div className="border-bottom pb-2 mb-2">
                                <div className="text-muted fs-12 mb-1">Assigned To</div>
                                <div className="d-flex flex-wrap gap-1">
                                    {event.assignedTo.map((person, index) => (
                                        <Badge key={index} bg="secondary" className="fs-12">
                                            {person}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    )}

                    {event.description && (
                        <Col sm={12}>
                            <div className="text-muted fs-12 mb-1">Description</div>
                            <p className="fs-14 mb-0">{event.description}</p>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button 
                    variant="outline-danger" 
                    onClick={() => {
                        if (onDelete) onDelete();
                    }}
                >
                    <i className="fas fa-trash me-2"></i>Delete
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                        Close
                    </Button>
                    <Button 
                        variant="primary"
                        onClick={() => {
                            if (onEdit) onEdit();
                        }}
                    >
                        <i className="fas fa-edit me-2"></i>Edit Event
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
